import { useQueries, useQueryClient } from '@tanstack/react-query';
import { blogTemplatesService, BlogTemplateSummary } from '@/services/blogTemplatesService';
import { blogCreationService } from '@/services/blogCreationService';
import { contentPlannerService } from '@/services/contentPlannerService';
import { Content } from '@/components/BlogCreationCommandCenter/types';
import { TopicalMapSummary } from '@/types/contentPlanner';

export interface SearchableTemplate {
  id: string;
  name: string;
  slug: string;
  description?: string;
  icon?: string;
  categories: string[];
  is_system: boolean;
}

export interface SearchableBlogPost {
  id: string;
  title: string;
  keyword: string;
  status: string;
}

export interface SearchableTopicalMap {
  id: string;
  seed_keyword: string;
  status: string;
  total_clusters: number;
}

export interface SearchData {
  templates: SearchableTemplate[];
  blogPosts: SearchableBlogPost[];
  topicalMaps: SearchableTopicalMap[];
  isLoading: boolean;
  error: string | null;
}

// Query keys for cache management
const SEARCH_DATA_KEYS = {
  all: ['search-data'] as const,
  templates: () => [...SEARCH_DATA_KEYS.all, 'templates'] as const,
  blogPosts: () => [...SEARCH_DATA_KEYS.all, 'blog-posts'] as const,
  topicalMaps: () => [...SEARCH_DATA_KEYS.all, 'topical-maps'] as const,
};

// Cache duration (5 minutes)
const STALE_TIME = 5 * 60 * 1000;

export function useSearchData(isOpen: boolean): SearchData {
  const results = useQueries({
    queries: [
      {
        queryKey: SEARCH_DATA_KEYS.templates(),
        queryFn: async (): Promise<SearchableTemplate[]> => {
          const response = await blogTemplatesService.list();
          return response.templates.map((t: BlogTemplateSummary) => ({
            id: t.id,
            name: t.name,
            slug: t.slug,
            description: t.description,
            icon: t.icon,
            categories: t.categories,
            is_system: t.is_system,
          }));
        },
        staleTime: STALE_TIME,
        enabled: isOpen,
      },
      {
        queryKey: SEARCH_DATA_KEYS.blogPosts(),
        queryFn: async (): Promise<SearchableBlogPost[]> => {
          const response = await blogCreationService.list({ page_size: 15 });
          return response.items
            .filter((item: Content) => !item.is_deleted)
            .map((item: Content) => ({
              id: item.id,
              title: item.title || item.keyword,
              keyword: item.keyword,
              status: item.status,
            }));
        },
        staleTime: STALE_TIME,
        enabled: isOpen,
      },
      {
        queryKey: SEARCH_DATA_KEYS.topicalMaps(),
        queryFn: async (): Promise<SearchableTopicalMap[]> => {
          const response = await contentPlannerService.listMaps({ limit: 50 });
          return response.maps.map((m: TopicalMapSummary) => ({
            id: m.map_id,
            seed_keyword: m.seed_keyword,
            status: m.status,
            total_clusters: m.total_clusters,
          }));
        },
        staleTime: STALE_TIME,
        enabled: isOpen,
      },
    ],
  });

  const [templatesQuery, blogPostsQuery, topicalMapsQuery] = results;

  // Combine loading states - loading if any are loading and none have data yet
  const isLoading = results.some((q) => q.isLoading);

  // Combine errors
  const error = results.find((q) => q.error)?.error?.message || null;

  return {
    templates: templatesQuery.data || [],
    blogPosts: blogPostsQuery.data || [],
    topicalMaps: topicalMapsQuery.data || [],
    isLoading,
    error,
  };
}

// Function to invalidate the cache (call this after creating/updating content)
export function useInvalidateSearchCache() {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: SEARCH_DATA_KEYS.all });
  };
}

// Legacy function name for backward compatibility
export function invalidateSearchCache() {
  // This is a no-op now - consumers should use useInvalidateSearchCache hook instead
  // The cache will automatically refresh based on staleTime
  console.warn('invalidateSearchCache() is deprecated. Use useInvalidateSearchCache() hook instead.');
}
